import { analyzeAiMessageRunQuery } from "@domain/ai_answers"
import { AiAuditReport } from "@domain/ai_answers/audit_report"
import CloseIcon from "@mui/icons-material/Close"
import ReplayIcon from "@mui/icons-material/Replay"
import SendIcon from "@mui/icons-material/Send"
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useEffect, useRef, useState } from "react"
import AiVerdictReport from "./AiVerdictReport"

interface Props {
  open: boolean
  onClose: () => void
  aiMessageId: string
}

const QUESTION_SUGGESTIONS = [
  "Doğru aksiyonu mu seçti?",
  "Cevaptaki bilgiler nereden geldi?",
  "Hangi prompt kuralı bu davranışa yol açtı?",
  "Bilgi kaynaklarını doğru kullandı mı?"
]

export default function AiAnalysisDialog({
  open,
  onClose,
  aiMessageId
}: Props) {
  const [question, setQuestion] = useState("")
  const [askedQuestion, setAskedQuestion] = useState<string | null>(null)
  const [report, setReport] = useState<AiAuditReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const defaultCache = useRef<Map<string, AiAuditReport>>(new Map())
  const requestSeq = useRef(0)

  const runAnalysis = async (customQuestion?: string) => {
    const trimmed = customQuestion?.trim()

    if (!trimmed) {
      const cached = defaultCache.current.get(aiMessageId)
      if (cached) {
        setAskedQuestion(null)
        setReport(cached)
        setError(null)
        return
      }
    }

    const seq = ++requestSeq.current
    setIsLoading(true)
    setError(null)
    setReport(null)
    setAskedQuestion(trimmed ?? null)

    const response = await analyzeAiMessageRunQuery({
      aiMessageId,
      ...(trimmed ? { question: trimmed } : {})
    })

    if (seq !== requestSeq.current) return
    setIsLoading(false)

    if (response.isSuccess && response.data) {
      setReport(response.data)
      if (!trimmed) {
        defaultCache.current.set(aiMessageId, response.data)
      }
    } else {
      setError(response.errorMessage ?? "Analiz alınamadı.")
    }
  }

  // Opening the dialog is itself the request for the verdict — no extra click to see it.
  useEffect(() => {
    if (!open || !aiMessageId) return
    setQuestion("")
    setAskedQuestion(null)
    setError(null)
    const cached = defaultCache.current.get(aiMessageId)
    setReport(cached ?? null)
    if (!cached) {
      void runAnalysis()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aiMessageId])

  const handleAsk = () => {
    if (!question.trim() || isLoading) return
    void runAnalysis(question)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1
        }}
      >
        <Box>
          <Typography variant="h6" fontSize={16}>
            Bu karar doğru mu?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tüm adımlar üzerinden karar denetimi · {aiMessageId}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 320 }}>
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 6
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              {askedQuestion
                ? "Soru, çalıştırmanın tüm adımları üzerinden yanıtlanıyor…"
                : "Adımlar denetleniyor, bu 30-60 sn sürebilir…"}
            </Typography>
          </Box>
        )}

        {error && !isLoading && (
          <Typography variant="body2" sx={{ color: "error.main", py: 2 }}>
            {error}
          </Typography>
        )}

        {report && !isLoading && (
          <AiVerdictReport report={report} question={askedQuestion} />
        )}

        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Bu çalıştırma hakkında soru sor…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  handleAsk()
                }
              }}
            />
            <Tooltip title="Soruyu sor" arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={handleAsk}
                  disabled={isLoading || !question.trim()}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {askedQuestion && (
              <Tooltip title="Genel karar denetimine dön" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={isLoading}
                    onClick={() => {
                      setQuestion("")
                      void runAnalysis()
                    }}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {QUESTION_SUGGESTIONS.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                size="small"
                variant="outlined"
                disabled={isLoading}
                onClick={() => {
                  setQuestion(suggestion)
                  void runAnalysis(suggestion)
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
